import type { components } from "../../../api/generated";
import type {
  DimHousLevelOption,
  GroundOption,
  GuideProviderOption,
  SupplierAttachment,
  SupplierItem,
  SupplierKind,
  SupplierListPage,
  SupplierListParams,
  SupplierMutation,
  UserOption,
} from "../types";

export type GuideProviderOptionRaw = components["schemas"]["GuideProviderOption"];

/** 映射导游提供或派遣主体候选。 */
export function mapGuideProviderOption(raw: GuideProviderOptionRaw): GuideProviderOption {
  return {
    supplierId: raw.supplier_id,
    supplierName: raw.supplier_name,
    supplierType: raw.supplier_type,
    supplierTypeLabel: raw.supplier_type_label,
  };
}

export type SupplierItemRaw = {
  supplier_id: string;
  supplier_code: string;
  supplier_name: string;
  supplier_short_name?: string | null;
  supplier_type: string;
  supplier_type_label: string;
  subject_nature?: "organization" | "individual" | "internal" | "unknown";
  province?: string | null;
  city?: string | null;
  district?: string | null;
  address_detail?: string | null;
  company_subject_name?: string | null;
  credit_code?: string | null;
  tax_no?: string | null;
  business_scope_desc?: string | null;
  parent_supplier_id?: string | null;
  parent_supplier_name?: string | null;
  can_be_settlement_subject: boolean;
  can_be_fulfillment_subject: boolean;
  can_be_quote_subject: boolean;
  cooperation_status: string;
  is_disabled: boolean;
  is_disabled_label: string;
  remark: string;
  created_at: string;
  updated_at: string;
  contact?: {
    contact_name?: string | null;
    mobile?: string | null;
    phone?: string | null;
    wechat?: string | null;
  } | null;
  attachments?: Array<{
    supplier_attachment_id?: string | null;
    attachment_type: string;
    media_scene?: string | null;
    attachment_name?: string;
    file_url: string;
    content_text?: string | null;
    sort_no?: number | null;
  }>;
  guide?: {
    guide_credential_no?: string | null;
    academic?: string | null;
    guide_resume?: string | null;
    team_experience?: string | null;
    bound_user_id?: number | null;
    bound_user_name?: string | null;
    remark?: string;
  } | null;
  bus?: {
    scale_text?: string | null;
    price_ref_text?: string | null;
    navigation_address?: string | null;
    fax?: string | null;
    protocol_name?: string | null;
    remark?: string;
  } | null;
  hotel?: {
    hous_level_id?: string | null;
    hous_level_name?: string | null;
    is_sketching_base?: boolean;
    navigation_address?: string | null;
    introduce_text?: string | null;
    price_ref_text?: string | null;
    receive_count_text?: string | null;
    last_decorate_date?: string | null;
    food_info_text?: string | null;
    nearby_text?: string | null;
    meet_room_info_text?: string | null;
    fax?: string | null;
    route_briefing_remark?: string | null;
    hotel_remark?: string | null;
    remark?: string;
  } | null;
  ground?: {
    describe_text?: string | null;
    scale_text?: string | null;
    remark?: string;
  } | null;
};

export type SupplierListPageRaw = {
  items: SupplierItemRaw[];
  total: number;
  page: number;
  page_size: number;
};

/** 映射列表查询参数。 */
export function mapSupplierListQueryParams(params: SupplierListParams) {
  const query: Record<string, string | number | boolean> = {
    page: params.page,
    page_size: params.pageSize,
  };
  const optionalTextParams = {
    q: normalizeOptionalText(params.q),
    province: normalizeOptionalText(params.province),
    city: normalizeOptionalText(params.city),
    subject_nature: params.subjectNature,
    cooperation_status: params.cooperationStatus,
  };
  for (const [key, value] of Object.entries(optionalTextParams)) {
    if (value !== undefined) query[key] = value;
  }
  const optionalBooleanParams = {
    is_disabled: params.isDisabled,
    can_be_fulfillment_subject: params.canBeFulfillmentSubject,
    can_be_settlement_subject: params.canBeSettlementSubject,
    can_be_quote_subject: params.canBeQuoteSubject,
  };
  for (const [key, value] of Object.entries(optionalBooleanParams)) {
    if (value !== undefined) query[key] = value;
  }
  return query;
}

/** 映射创建/更新载荷（按品类只提交对应扩展段）。 */
export function mapSupplierMutationPayload(kind: SupplierKind, payload: SupplierMutation) {
  const base = {
    supplier_name: payload.supplierName.trim(),
    supplier_short_name: normalizeOptionalText(payload.supplierShortName),
    province: normalizeOptionalText(payload.province),
    city: normalizeOptionalText(payload.city),
    district: normalizeOptionalText(payload.district),
    address_detail: normalizeOptionalText(payload.addressDetail),
    company_subject_name: normalizeOptionalText(payload.companySubjectName),
    credit_code: normalizeOptionalText(payload.creditCode),
    tax_no: normalizeOptionalText(payload.taxNo),
    business_scope_desc: normalizeOptionalText(payload.businessScopeDesc),
    parent_supplier_id: normalizeOptionalText(payload.parentSupplierId),
    can_be_settlement_subject: payload.canBeSettlementSubject,
    can_be_fulfillment_subject: payload.canBeFulfillmentSubject,
    can_be_quote_subject: payload.canBeQuoteSubject,
    remark: payload.remark.trim(),
  };
  if (kind === "guides") {
    return {
      ...base,
      subject_nature: payload.subjectNature,
      guide: {
        guide_credential_no:
          payload.subjectNature === "individual"
            ? normalizeOptionalText(payload.guide.guideCredentialNo)
            : undefined,
        academic: normalizeOptionalText(payload.guide.academic),
        guide_resume: normalizeOptionalText(payload.guide.guideResume),
        team_experience: normalizeOptionalText(payload.guide.teamExperience),
        bound_user_id:
          payload.subjectNature === "individual"
            ? payload.guide.boundUserId ?? undefined
            : undefined,
        remark: payload.guide.remark.trim(),
      },
    };
  }
  if (kind === "buses") {
    return {
      ...base,
      bus: {
        scale_text: normalizeOptionalText(payload.bus.scaleText),
        price_ref_text: normalizeOptionalText(payload.bus.priceRefText),
        navigation_address: normalizeOptionalText(payload.bus.navigationAddress),
        fax: normalizeOptionalText(payload.bus.fax),
        protocol_name: normalizeOptionalText(payload.bus.protocolName),
        remark: payload.bus.remark.trim(),
      },
    };
  }
  if (kind === "hotels" || kind === "restaurants") {
    return base;
  }
  return {
    ...base,
    ground: {
      describe_text: normalizeOptionalText(payload.ground.describeText),
      scale_text: normalizeOptionalText(payload.ground.scaleText),
      remark: payload.ground.remark.trim(),
    },
  };
}

/** 映射列表响应。 */
export function mapSupplierListPage(raw: SupplierListPageRaw): SupplierListPage {
  return {
    items: (raw.items ?? []).map(mapSupplierItem),
    total: raw.total,
    page: raw.page,
    pageSize: raw.page_size,
  };
}

/** 映射单项响应。 */
export function mapSupplierItem(raw: SupplierItemRaw): SupplierItem {
  return {
    supplierId: raw.supplier_id,
    supplierCode: raw.supplier_code,
    supplierName: raw.supplier_name,
    supplierShortName: raw.supplier_short_name ?? undefined,
    supplierType: raw.supplier_type,
    supplierTypeLabel: raw.supplier_type_label,
    subjectNature: raw.subject_nature ?? "unknown",
    province: raw.province ?? undefined,
    city: raw.city ?? undefined,
    district: raw.district ?? undefined,
    addressDetail: raw.address_detail ?? undefined,
    companySubjectName: raw.company_subject_name ?? undefined,
    creditCode: raw.credit_code ?? undefined,
    taxNo: raw.tax_no ?? undefined,
    businessScopeDesc: raw.business_scope_desc ?? undefined,
    parentSupplierId: raw.parent_supplier_id ?? undefined,
    parentSupplierName: raw.parent_supplier_name ?? undefined,
    canBeSettlementSubject: raw.can_be_settlement_subject,
    canBeFulfillmentSubject: raw.can_be_fulfillment_subject,
    canBeQuoteSubject: raw.can_be_quote_subject,
    cooperationStatus: raw.cooperation_status,
    isDisabled: raw.is_disabled,
    isDisabledLabel: raw.is_disabled_label,
    remark: raw.remark,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    contact: raw.contact
      ? {
          contactName: raw.contact.contact_name ?? "",
          mobile: raw.contact.mobile ?? "",
          phone: raw.contact.phone ?? "",
          wechat: raw.contact.wechat ?? "",
        }
      : undefined,
    attachments: (raw.attachments ?? []).map(mapSupplierAttachment),
    guide: raw.guide
      ? {
          guideCredentialNo: raw.guide.guide_credential_no ?? undefined,
          academic: raw.guide.academic ?? undefined,
          guideResume: raw.guide.guide_resume ?? undefined,
          teamExperience: raw.guide.team_experience ?? undefined,
          boundUserId: raw.guide.bound_user_id ?? undefined,
          boundUserName: raw.guide.bound_user_name ?? undefined,
          remark: raw.guide.remark ?? "",
        }
      : undefined,
    bus: raw.bus
      ? {
          scaleText: raw.bus.scale_text ?? undefined,
          priceRefText: raw.bus.price_ref_text ?? undefined,
          navigationAddress: raw.bus.navigation_address ?? undefined,
          fax: raw.bus.fax ?? undefined,
          protocolName: raw.bus.protocol_name ?? undefined,
          remark: raw.bus.remark ?? "",
        }
      : undefined,
    hotel: raw.hotel
      ? {
          housLevelId: raw.hotel.hous_level_id ?? undefined,
          housLevelName: raw.hotel.hous_level_name ?? undefined,
          isSketchingBase: Boolean(raw.hotel.is_sketching_base),
          navigationAddress: raw.hotel.navigation_address ?? undefined,
          introduceText: raw.hotel.introduce_text ?? undefined,
          priceRefText: raw.hotel.price_ref_text ?? undefined,
          receiveCountText: raw.hotel.receive_count_text ?? undefined,
          lastDecorateDate: raw.hotel.last_decorate_date ?? undefined,
          foodInfoText: raw.hotel.food_info_text ?? undefined,
          nearbyText: raw.hotel.nearby_text ?? undefined,
          meetRoomInfoText: raw.hotel.meet_room_info_text ?? undefined,
          fax: raw.hotel.fax ?? undefined,
          routeBriefingRemark: raw.hotel.route_briefing_remark ?? undefined,
          hotelRemark: raw.hotel.hotel_remark ?? undefined,
          remark: raw.hotel.remark ?? "",
        }
      : undefined,
    ground: raw.ground
      ? {
          describeText: raw.ground.describe_text ?? undefined,
          scaleText: raw.ground.scale_text ?? undefined,
          remark: raw.ground.remark ?? "",
        }
      : undefined,
  };
}

/** 映射酒店档次选项。 */
export function mapDimHousLevelOption(raw: {
  hous_level_id: string;
  level_code: string;
  level_name: string;
  level_short_name?: string | null;
}): DimHousLevelOption {
  return {
    housLevelId: raw.hous_level_id,
    levelCode: raw.level_code,
    levelName: raw.level_name,
    levelShortName: raw.level_short_name ?? undefined,
  };
}

/** 映射地接供应商选项。 */
export function mapGroundOption(raw: {
  supplier_id: string;
  supplier_name: string;
  supplier_short_name?: string | null;
}): GroundOption {
  return {
    supplierId: raw.supplier_id,
    supplierName: raw.supplier_name,
    supplierShortName: raw.supplier_short_name ?? undefined,
  };
}

/** 映射用户选项。 */
export function mapUserOption(raw: {
  user_id: number;
  display_name: string;
  phone?: string | null;
}): UserOption {
  return {
    userId: raw.user_id,
    displayName: raw.display_name,
    phone: raw.phone ?? undefined,
  };
}

/** 映射供应商附件响应。 */
function mapSupplierAttachment(raw: {
  supplier_attachment_id?: string | null;
  attachment_type: string;
  media_scene?: string | null;
  attachment_name?: string;
  file_url: string;
  content_text?: string | null;
  sort_no?: number | null;
}): SupplierAttachment {
  return {
    supplierAttachmentId: raw.supplier_attachment_id ?? undefined,
    attachmentType: raw.attachment_type,
    mediaScene: raw.media_scene ?? undefined,
    attachmentName: raw.attachment_name ?? "",
    fileUrl: raw.file_url,
    contentText: raw.content_text ?? undefined,
    sortNo: raw.sort_no ?? undefined,
  };
}

/** 规范化可选文本并移除首尾空白。 */
function normalizeOptionalText(value?: string) {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
}
